import { useState } from 'react';
import type { CategorySourcesDto, SourceInfoDto } from '../types';
import './StatusPage.css';

interface SourceWeightsPanelProps {
  categories: CategorySourcesDto[];
  selectedCategory: string | null;
  selectedSubCategory: string | null;
}

interface GroupedSources {
  categoryName: string;
  subCategoryName: string | null;
  sources: SourceInfoDto[];
}

export function SourceWeightsPanel({ categories, selectedCategory, selectedSubCategory }: SourceWeightsPanelProps) {
  // Filter categories based on selection
  const filteredCategories = categories.filter(c => {
    if (selectedCategory && c.categorySlug !== selectedCategory) return false;
    if (selectedSubCategory && c.subCategorySlug !== selectedSubCategory) return false;
    return true;
  });

  // Group sources by category and subcategory
  const groupedSources: GroupedSources[] = filteredCategories.map(category => ({
    categoryName: category.categoryName,
    subCategoryName: category.subCategoryName,
    sources: [...category.sources].sort((a, b) => b.weight - a.weight),
  }));

  // State for collapsible sections
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(groupedSources.map((_, index) => `group-${index}`))
  );

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const getWeightColor = (weight: number): string => {
    if (weight >= 0.85) return 'high';
    if (weight >= 0.70) return 'medium';
    return 'low';
  };

  const getWeightLabel = (weight: number): string => {
    if (weight >= 0.85) return 'High Authority';
    if (weight >= 0.70) return 'Medium Authority';
    return 'Lower Authority';
  };

  const renderSourcesList = (sources: SourceInfoDto[]) => (
    <div className="source-weights-panel__list">
      {sources.map((source) => {
        const weightClass = getWeightColor(source.weight);
        const weightLabel = getWeightLabel(source.weight);
        const weightPercent = source.weight * 100;

        return (
          <div
            key={source.slug}
            className={`source-weights-panel__item source-weights-panel__item--${weightClass}`}
          >
            <div className="source-weights-panel__item-header">
              <div className="source-weights-panel__item-info">
                <span className="source-weights-panel__source-name">{source.name}</span>
                <span className="source-weights-panel__weight-label">{weightLabel}</span>
              </div>
              <div className="source-weights-panel__weight-value">
                <span className="source-weights-panel__weight-number">{source.weight.toFixed(2)}</span>
                <span className="source-weights-panel__weight-badge" data-weight={weightClass}>
                  {weightPercent.toFixed(0)}%
                </span>
              </div>
            </div>
            <div className="source-weights-panel__progress-bar">
              <div
                className={`source-weights-panel__progress-fill source-weights-panel__progress-fill--${weightClass}`}
                style={{ width: `${weightPercent}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="source-weights-panel">
      <div className="source-weights-panel__intro">
        <div className="source-weights-panel__header">
          <h3 className="source-weights-panel__title">Source Authority Weights</h3>
          <p className="source-weights-panel__subtitle">
            Source weight contributes 30% to the Interest Score algorithm
          </p>
        </div>

        <div className="source-weights-panel__legend">
          <div className="source-weights-panel__legend-item">
            <span className="source-weights-panel__legend-badge source-weights-panel__legend-badge--high">
              High
            </span>
            <span className="source-weights-panel__legend-text">&ge; 0.85</span>
          </div>
          <div className="source-weights-panel__legend-item">
            <span className="source-weights-panel__legend-badge source-weights-panel__legend-badge--medium">
              Medium
            </span>
            <span className="source-weights-panel__legend-text">0.70 - 0.84</span>
          </div>
          <div className="source-weights-panel__legend-item">
            <span className="source-weights-panel__legend-badge source-weights-panel__legend-badge--low">
              Lower
            </span>
            <span className="source-weights-panel__legend-text">&lt; 0.70</span>
          </div>
        </div>
      </div>

      {groupedSources.length === 0 && (
        <div className="source-weights-panel__empty">
          <p>No source data available for the selected filters</p>
        </div>
      )}

      {groupedSources.map((group, index) => {
        const groupId = `group-${index}`;
        const isExpanded = expandedGroups.has(groupId);
        const groupTitle = group.subCategoryName
          ? `${group.categoryName} > ${group.subCategoryName}`
          : group.categoryName;

        return (
          <div key={groupId} className="source-weights-panel__group">
            <div
              className="source-weights-panel__group-header"
              onClick={() => toggleGroup(groupId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleGroup(groupId);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <h4 className="source-weights-panel__group-title">{groupTitle}</h4>
              <div className="source-weights-panel__group-info">
                <span className="source-weights-panel__group-count">
                  {group.sources.length} sources
                </span>
                <button
                  className="source-weights-panel__group-toggle"
                  aria-expanded={isExpanded}
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                  {isExpanded ? '−' : '+'}
                </button>
              </div>
            </div>

            {isExpanded && renderSourcesList(group.sources)}
          </div>
        );
      })}
    </div>
  );
}
